import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Row, Col, Card, Badge, Button,
    Spinner, Alert
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthGetRequest } from '../Backend/APIMiddleware';
import { convertToLocalTime } from '../Backend/BackgroundTasks/convertutclocal';

// ── Priority config ───────────────────────────────────────────────────────────
const PRIORITY_LABEL = { 1: 'Urgent', 2: 'High', 3: 'Standard', 4: 'Low', 5: 'Whenever' };
const PRIORITY_VARIANT = { 1: 'danger', 2: 'warning', 3: 'primary', 4: 'success', 5: 'secondary' };

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (utcStr) => {
    if (!utcStr) return null;
    return String(convertToLocalTime(utcStr));
};

const isOverdue = (utcStr) => {
    if (!utcStr) return false;
    return new Date(utcStr) < new Date();
};

const isDueToday = (utcStr) => {
    if (!utcStr) return false;
    const d = new Date(utcStr);
    const now = new Date();
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
};

// ── Repair card ───────────────────────────────────────────────────────────────
function RepairCard({ repair, onClick }) {
    const { t } = useTranslation();
    const overdue   = isOverdue(repair.promisedDate);
    const dueToday  = !overdue && isDueToday(repair.promisedDate);

    return (
        <Card
            className="mb-3 shadow-sm"
            style={{ cursor: 'pointer', borderLeft: `4px solid var(--bs-${PRIORITY_VARIANT[repair.priority] ?? 'secondary'})` }}
            onClick={() => onClick(repair.transactionId)}
        >
            <Card.Body className="py-2 px-3">
                {/* Top row: ticket ID + priority badge */}
                <div className="d-flex justify-content-between align-items-start mb-1">
                    <span className="fw-bold text-primary" style={{ fontSize: '0.9rem' }}>
                        {repair.ticketId || repair.transactionId?.slice(0, 8)}
                    </span>
                    <Badge bg={PRIORITY_VARIANT[repair.priority] ?? 'secondary'} style={{ fontSize: '0.7rem' }}>
                        P{repair.priority} {t(PRIORITY_LABEL[repair.priority] ?? '')}
                    </Badge>
                </div>

                {/* Customer name */}
                <div className="fw-semibold mb-1" style={{ fontSize: '0.95rem' }}>
                    {repair.customerName || t('Unknown Customer')}
                </div>

                {/* Notes preview */}
                {repair.transactionNotes && (
                    <div
                        className="text-muted mb-1"
                        style={{
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}
                        title={repair.transactionNotes}
                    >
                        {repair.transactionNotes}
                    </div>
                )}

                {/* Footer row: promised date + item count */}
                <div className="d-flex justify-content-between align-items-center mt-1">
                    <small
                        className={
                            overdue  ? 'text-danger fw-bold' :
                            dueToday ? 'text-warning fw-bold' :
                            'text-muted'
                        }
                    >
                        {repair.promisedDate
                            ? (overdue   ? '⚠ ' : dueToday ? '⏰ ' : '📅 ') + formatDate(repair.promisedDate)
                            : <span className="text-muted">{t('No promise date')}</span>
                        }
                    </small>
                    {repair.totalItems > 0 && (
                        <small className="text-muted">
                            {repair.totalItems} {t('item', { count: repair.totalItems })}
                        </small>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
}

// ── Column ────────────────────────────────────────────────────────────────────
function QueueColumn({ title, variant, icon, repairs, onCardClick, isLoading }) {
    const { t } = useTranslation();

    return (
        <Card className="h-100 shadow-sm">
            <Card.Header className={`bg-${variant} text-white d-flex justify-content-between align-items-center`}>
                <span>{icon} {t(title)}</span>
                <Badge bg="light" text="dark">{repairs.length}</Badge>
            </Card.Header>
            <Card.Body
                style={{
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 220px)',
                    minHeight: 200,
                }}
            >
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : repairs.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <div style={{ fontSize: '2rem' }}>✅</div>
                        <small>{t('Nothing here')}</small>
                    </div>
                ) : (
                    repairs.map((r) => (
                        <RepairCard key={r.transactionId} repair={r} onClick={onCardClick} />
                    ))
                )}
            </Card.Body>
        </Card>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function RepairQueue() {
    const { t }    = useTranslation();
    const navigate = useNavigate();

    const [repairs,   setRepairs]   = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error,     setError]     = useState(null);
    const [lastFetch, setLastFetch] = useState(null);

    const fetchQueue = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await AuthGetRequest('/v1/transactions/repair/queue');
            setRepairs(Array.isArray(response.data) ? response.data : []);
            setLastFetch(new Date());
        } catch (e) {
            setError(e?.response?.data || e?.message || t('Failed to load repair queue'));
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    // Split into two columns
    const inQueue      = repairs.filter(r => r.transactionStatus === 'In Queue');
    const onWorkbench  = repairs.filter(r => r.transactionStatus === 'On the Workbench');

    const handleCardClick = (transactionId) => {
        navigate(`/transactions/repair/${transactionId}`);
    };

    return (
        <Container fluid className="mt-3 px-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">🔧 {t('Repair Queue')}</h4>
                    {lastFetch && (
                        <small className="text-muted">
                            {t('Last updated')}: {lastFetch.toLocaleTimeString()}
                        </small>
                    )}
                </div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchQueue}
                    disabled={isLoading}
                >
                    {isLoading ? <Spinner animation="border" size="sm" /> : '↻ ' + t('Refresh')}
                </Button>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Legend */}
            <div className="d-flex gap-3 mb-3 flex-wrap">
                <small><Badge bg="danger">P1 Urgent</Badge></small>
                <small><Badge bg="warning" text="dark">P2 High</Badge></small>
                <small><Badge bg="primary">P3 Standard</Badge></small>
                <small><Badge bg="success">P4 Low</Badge></small>
                <small><Badge bg="secondary">P5 Whenever</Badge></small>
                <small className="text-muted ms-2">⚠ = Overdue &nbsp;|&nbsp; ⏰ = Due Today &nbsp;|&nbsp; 📅 = Upcoming</small>
            </div>

            {/* Kanban columns */}
            <Row className="g-3">
                <Col md={6}>
                    <QueueColumn
                        title="In Queue"
                        variant="info"
                        icon="🗂"
                        repairs={inQueue}
                        onCardClick={handleCardClick}
                        isLoading={isLoading}
                    />
                </Col>
                <Col md={6}>
                    <QueueColumn
                        title="On the Workbench"
                        variant="warning"
                        icon="⚒"
                        repairs={onWorkbench}
                        onCardClick={handleCardClick}
                        isLoading={isLoading}
                    />
                </Col>
            </Row>

            {/* Summary footer */}
            {!isLoading && repairs.length > 0 && (
                <div className="text-center text-muted mt-3">
                    <small>
                        {repairs.length} {t('active repair', { count: repairs.length })} &mdash;&nbsp;
                        {inQueue.length} {t('queued')},&nbsp;
                        {onWorkbench.length} {t('on bench')}
                    </small>
                </div>
            )}
        </Container>
    );
}
